// ENG-5932 (Reaper Slice 1) — criticality classification + a local,
// persistent channel-quarantine store.
//
// Why this exists. Each managed Claude Code agent runs as a persistent tmux
// session declaring MCP *channel* servers (telegram / slack / msteams /
// direct-chat) and *tool* servers (composio_* / xero). The mcp-presence-reaper
// restarts the WHOLE session when any declared child is missing. When one
// channel is permanently broken (e.g. Kylie's orphaned msteams config — the
// teams-channel MCP can never start), the reaper restarts the session forever,
// SIGINT-ing the healthy sibling channels (Telegram) every cycle. The agent
// drops messages on channels that work because of one that never will.
//
// The fix (council-reviewed, ENG-5931): after a bounded restart budget, instead
// of restarting the session AGAIN, QUARANTINE the dead optional channel — drop
// it from the agent's provisioned set and stop restarting for it — while never
// touching ESSENTIAL channels (direct-chat / augmented are the control plane).
//
// Two hard guardrails are encoded here:
//
//   1. FAIL CLOSED. Criticality is read from a server-declared field on the
//      .mcp.json entry when present (Slice 2's provisioning will write it).
//      Absent that, only a known, hard-coded set of channel keys is treated as
//      OPTIONAL (quarantinable). Anything else — unknown channels, tool
//      servers, anything we can't positively identify — is ESSENTIAL and is
//      NEVER quarantined. Misclassifying direct-chat/augmented as optional
//      would be a permanent self-inflicted outage, so the essential set wins
//      even over a (buggy) declared "optional".
//
//   2. LOCAL-AUTHORITATIVE + PERSISTENT. The quarantine marker lives on local
//      disk and survives manager restarts. That persistence is what actually
//      kills the flap: once a channel is quarantined and dropped from
//      .mcp.json, a manager restart can't re-arm the reaper against it (the
//      reaper's in-memory restart counter resetting on restart was the second
//      root defect). No server round-trip is involved — with the API down, the
//      flap still stops. (Server-side observability is Slice 2.)

import { readFileSync } from 'node:fs';
import { join } from 'node:path';
import { atomicWriteFileSync } from './atomic-write.js';

export type ChannelCriticality = 'essential' | 'optional';

/**
 * Channels that must NEVER be quarantined — the agent's control plane. The
 * essential set wins even over a server-declared `criticality: 'optional'`, so
 * a provisioning bug can't laundered-quarantine the agent's own lifeline.
 */
const ESSENTIAL_CHANNEL_KEYS: ReadonlySet<string> = new Set(['direct-chat', 'augmented']);

/**
 * Known OPTIONAL (quarantinable) channel server keys — the fail-closed
 * fallback used when an entry carries no server-declared criticality field.
 * These are the canonical channel ids the manager writes into `.mcp.json`
 * (mcpServers keys), NOT derived from `_mcp/*-channel.js` filenames at runtime
 * — a filename glob is too loose to trust for a decision that drops a channel.
 *
 * Anything not in this set and not server-declared optional stays ESSENTIAL.
 * Adding a new optional channel is a deliberate one-line edit here.
 */
const OPTIONAL_CHANNEL_KEYS: ReadonlySet<string> = new Set([
  'telegram',
  'slack',
  'msteams',
  'discord',
  'whatsapp',
  'signal',
  'imessage',
  'matrix',
  'irc',
  'nostr',
]);

/**
 * Read a server-declared criticality off an `.mcp.json` server entry, if
 * present. Slice 2's provisioning will stamp `_augmented.criticality` onto each
 * channel server it writes; until then this is always absent and the fail-
 * closed fallback in {@link classifyChannelCriticality} governs. Returns
 * undefined for any unrecognised / malformed shape (treated as "no signal").
 */
function readDeclaredCriticality(entry: unknown): ChannelCriticality | undefined {
  if (!entry || typeof entry !== 'object') return undefined;
  const meta = (entry as { _augmented?: unknown })._augmented;
  if (!meta || typeof meta !== 'object') return undefined;
  const value = (meta as { criticality?: unknown }).criticality;
  return value === 'essential' || value === 'optional' ? value : undefined;
}

/**
 * Classify an `.mcp.json` server key as ESSENTIAL or OPTIONAL, FAIL CLOSED.
 *
 * Order of precedence:
 *   1. The hard-coded essential set always wins (direct-chat / augmented).
 *   2. A server-declared `_augmented.criticality` field (Slice 2).
 *   3. The known optional-channel fallback set.
 *   4. Everything else (unknown channels, tool servers, malformed entries) ⇒
 *      ESSENTIAL ⇒ never quarantined.
 *
 * `entry` is the raw value at `mcpServers[serverKey]` (may be undefined).
 */
export function classifyChannelCriticality(
  serverKey: string,
  entry?: unknown,
): ChannelCriticality {
  // (1) Essential set is non-negotiable — even a declared "optional" can't
  // demote the control-plane channels.
  if (ESSENTIAL_CHANNEL_KEYS.has(serverKey)) return 'essential';

  // (2) Trust an explicit server-declared field next.
  const declared = readDeclaredCriticality(entry);
  if (declared) return declared;

  // (3) Fail-closed fallback: only the known optional channels are optional.
  if (OPTIONAL_CHANNEL_KEYS.has(serverKey)) return 'optional';

  // (4) Unknown ⇒ ESSENTIAL ⇒ never quarantine.
  return 'essential';
}

export interface QuarantineEntry {
  /** Epoch ms when the channel was quarantined. */
  quarantinedAt: number;
  /** Human-readable reason (logged + surfaced in Slice 3's console badge). */
  reason: string;
}

/** On-disk shape: codeName → serverKey → entry. */
type QuarantineFile = Record<string, Record<string, QuarantineEntry>>;

/**
 * Default on-disk location for the quarantine marker file. Lives directly under
 * the manager's config dir (NOT under a per-agent dir), so it survives an agent
 * reprovision that rewrites `<configDir>/<codeName>/` — the persistence is the
 * whole point.
 */
export function defaultQuarantinePath(configDir: string): string {
  return join(configDir, 'channel-quarantine.json');
}

/**
 * Local, persistent quarantine store. Reads lazily from disk and caches; every
 * mutation writes atomically (tmp + rename) so a concurrent reader never sees a
 * torn file.
 *
 * Construct one per manager process with the resolved path. All reads are
 * cheap (in-memory after first load); the disk hit only happens on first access
 * and is re-validated on each mutation.
 */
export class ChannelQuarantineStore {
  private readonly path: string;
  private cache: QuarantineFile | null = null;

  constructor(path: string) {
    this.path = path;
  }

  private load(): QuarantineFile {
    if (this.cache) return this.cache;
    try {
      const raw = readFileSync(this.path, 'utf-8');
      const parsed = JSON.parse(raw) as unknown;
      this.cache = isQuarantineFile(parsed) ? parsed : {};
    } catch {
      // Missing / unreadable / unparseable → empty store. A corrupt file must
      // never wedge provisioning; worst case we re-discover and re-quarantine.
      this.cache = {};
    }
    return this.cache;
  }

  private persist(): void {
    atomicWriteFileSync(this.path, JSON.stringify(this.cache ?? {}, null, 2));
  }

  /** Is `serverKey` currently quarantined for `codeName`? */
  isQuarantined(codeName: string, serverKey: string): boolean {
    return this.load()[codeName]?.[serverKey] !== undefined;
  }

  /** The set of quarantined server keys for `codeName` (empty Set if none). */
  getQuarantinedKeys(codeName: string): Set<string> {
    const forAgent = this.load()[codeName];
    return new Set(forAgent ? Object.keys(forAgent) : []);
  }

  /**
   * Mark `serverKey` quarantined for `codeName`. Idempotent — re-quarantining
   * an already-quarantined key preserves the original `quarantinedAt` (so the
   * "how long has this been quarantined" escalation clock in Slice 3 isn't
   * reset by provisioning churn). Returns true iff this call newly quarantined
   * the key (false if it was already quarantined).
   */
  quarantine(codeName: string, serverKey: string, reason: string, now: number): boolean {
    const file = this.load();
    const forAgent = (file[codeName] ??= {});
    if (forAgent[serverKey]) return false;
    forAgent[serverKey] = { quarantinedAt: now, reason };
    this.persist();
    return true;
  }

  /**
   * Clear the quarantine for `serverKey` (explicit re-enable). Returns true iff
   * a marker was actually removed. NOTE: re-enable is an EXPLICIT verb (Slice 2
   * exposes it via the API/console) — provisioning's idempotent `.mcp.json`
   * rewrites must NEVER call this, or a quarantined channel would re-arm into a
   * fresh flap laundered through the config loop.
   */
  clearQuarantine(codeName: string, serverKey: string): boolean {
    const file = this.load();
    const forAgent = file[codeName];
    if (!forAgent || !forAgent[serverKey]) return false;
    delete forAgent[serverKey];
    if (Object.keys(forAgent).length === 0) delete file[codeName];
    this.persist();
    return true;
  }

  /** Test seam: drop the in-memory cache so the next read re-hits disk. */
  __invalidateCacheForTests(): void {
    this.cache = null;
  }
}

function isQuarantineFile(value: unknown): value is QuarantineFile {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return false;
  for (const forAgent of Object.values(value as Record<string, unknown>)) {
    if (!forAgent || typeof forAgent !== 'object' || Array.isArray(forAgent)) return false;
    for (const entry of Object.values(forAgent as Record<string, unknown>)) {
      if (!entry || typeof entry !== 'object') return false;
      const e = entry as Record<string, unknown>;
      if (typeof e.quarantinedAt !== 'number' || typeof e.reason !== 'string') return false;
    }
  }
  return true;
}
